import React, { useEffect, useState } from 'react';
import {
  View, StyleSheet, Text, Pressable, FlatList, ActivityIndicator,
} from 'react-native';
import Modal from 'react-native-modal';
import { FontAwesome } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Style from '../../constants/Style';
import PressableStyled, { PressableState } from '../PressableStyled';
import Colors from '../../constants/Colors';
import titleCase from '../TitleCase';
import CustomTextField from '../CustomTextField';
import nameRegex, { integerRegex, externalIdRegex } from '../Regex';
import Button from '../Button';
import CloseModalIcon from '../CloseModalIcon';
import AlertIcon12 from '../12pxIcons/AlertIcon12';
import { AreaType, LocationSubtypeTitles } from './SetFloorsModal/FloorItem';
import asNumber from '../AsNumber';
import { PROVIDE_CAPACITY } from '../../constants/AlertMessages';
import PriorityIndicator from '../../screens/LocationManagement/components/PriorityIndicator';

interface AddAreaModalProps {
  isVisible: boolean,
  hide: () => void,
  cancel: () => void,
  save: Function,
  isSpace: boolean
  loading: boolean,
  editing?: boolean,
  shapeName?: string,
  shapeIsHighPriority?: boolean,
  locationCapacity?: number,
  locationExternalId?: string,
  locationSubtype?: AreaType | null,
}

const maxNameLength: number = 50;

const AddAreaModal = ({
  isVisible, hide, isSpace = false, cancel, save, loading, editing = false, shapeName = '',
  shapeIsHighPriority, locationSubtype, locationExternalId, locationCapacity,
}: AddAreaModalProps) => {
  const [locationType, setLocationType] = useState<AreaType | null | undefined>(locationSubtype);
  const [locationTypeDropdownShown, setLocationTypeDrodpdownShown] = useState<boolean>(false);
  const [name, setName] = useState<string>(shapeName);
  const [capacity, setCapacity] = useState<number | null>(asNumber(locationCapacity));
  const [externalId, setExternalId] = useState<string | null>(locationExternalId);
  const [nameError, setNameError] = useState<string | null>(null);
  const [capacityError, setCapacityError] = useState<string | null>(null);
  const [externalIdError, setExternalIdError] = useState<string | null>(null);
  const [isHighPriority, setIsHighPriority] = useState<boolean>(shapeIsHighPriority);
  const [priorityDropdownShown, setPriorityDropdownShown] = useState<boolean>(false);
  const [clientId, setClientId] = useState('hereworks');
  const type: string = isSpace ? 'space' : 'area';
  const disabled: boolean = loading || !!nameError || !!externalIdError || !(name?.length > 0);

  const updateName = (text: string) => {
    const nameLength: number = text?.trim().length;
    if (!(nameLength > 0)) setNameError(`A name is required for a ${type}`);
    else if (nameLength > maxNameLength) {
      setNameError(`${titleCase(type)} name cannot exceed ${maxNameLength} characters`);
    } else if (!nameRegex.exp.test(text)) {
      setNameError(`${titleCase(type)} name${nameRegex.message}`);
    } else setNameError(null);
    setName(text);
  };

  const updateExternalId = (text: string) => {
    if (text?.trim().length > 0 && !externalIdRegex.exp.test(text.trim())) {
      setExternalIdError(externalIdRegex.message);
    } else setExternalIdError(null);
    setExternalId(text?.trim());
  };

  const updateCapacity = (text: string) => {
    const value: number | null = asNumber(text);
    if (text?.trim().length > 0 && !integerRegex.exp.test(text.trim())) {
      setCapacityError(`Capacity${integerRegex.message}`);
    } else if (locationType && !value) {
      setCapacityError(PROVIDE_CAPACITY);
    } else setCapacityError(null);
    setCapacity(value);
  };

  const onModalHide: () => void = () => {
    setLocationTypeDrodpdownShown(false);
    setName(editing ? shapeName : '');
    setNameError(null);
    setIsHighPriority(editing && shapeIsHighPriority);
    setPriorityDropdownShown(false);
    setLocationType(editing ? locationSubtype : null);
    setCapacity(editing ? asNumber(locationCapacity) : null);
    setCapacityError(null);
    setExternalId(editing ? locationExternalId : null);
    setExternalIdError(null);
    cancel();
  };

  useEffect(() => setName(shapeName), [shapeName]);

  useEffect(() => setIsHighPriority(shapeIsHighPriority), [shapeIsHighPriority]);

  useEffect(() => setExternalId(locationExternalId), [locationExternalId]);

  useEffect(() => {
    AsyncStorage.getItem('clientName').then((n) => {
      setClientId(n);
    });
  }, []);

  return <Modal
    {...{ isVisible, onModalHide }}
    onModalWillShow={onModalHide}
    animationIn={'fadeIn'}
    animationOut={'fadeOut'}
    onBackButtonPress={hide}
    onBackdropPress={hide}
    onSwipeComplete={hide}
    style={Style.modal}
  >
    <View style={[Style.modalContent, styles.container]}>
      <PressableStyled
        style={Style.closeButton}
        onPress={hide}
      >
        <CloseModalIcon />
      </PressableStyled>
      <Text style={styles.title}>{`${editing ? 'Edit' : 'Create'} ${type}`}</Text>
      <Text style={[styles.text, styles.subtitle]}>{'Fields marked with * are mandatory'}</Text>
      <View style={{ zIndex: 2 }}>
        <CustomTextField
          placeholder='Enter name'
          title='Name'
          maxLength={maxNameLength}
          required
          invalid={!!nameError}
          errorText={nameError}
          value={name}
          updateValue={updateName}
        />
        <PriorityIndicator
          {...{
            isHighPriority, setIsHighPriority, priorityDropdownShown, setPriorityDropdownShown,
          }}
          style={styles.priorityIndicator}
          testID='create-area-modal-priority-picker'
        />
      </View>
      {(!editing || locationSubtype) && <>
        <Text style={styles.text}>{'Location type'}</Text>
        <PressableStyled
          style={styles.locationTypeDropdownButton}
          onPress={() => setLocationTypeDrodpdownShown(!locationTypeDropdownShown)}
          disabled={editing}
          testID='select-location-type-btn'
        >
          <Text selectable={locationType !== null}>
            {locationType === null
              ? 'Select a location type'
              : (LocationSubtypeTitles[locationType] ?? 'Miscellaneous')}
          </Text>
          {!editing && <FontAwesome
            size={24}
            style={{ color: Colors.light.hereworksBlue500 }}
            name={locationTypeDropdownShown ? 'angle-up' : 'angle-down'}
          />}
        </PressableStyled>
      </>}
      <View style={{ zIndex: 2 }}>
        {locationTypeDropdownShown && (!editing || locationSubtype) && <FlatList
          testID='location-type-list'
          style={styles.locationTypeDropdown}
          // data={[null, ...(parentIsMeetingArea ? [AreaType.MEETING_ROOM, AreaType.QUEUE]
          //   : Object.values(AreaType)
          //     .filter((key: AreaType) => !(isSpace && !parentIsMeetingArea
          //       && key === AreaType.MEETING_ROOM)))]}
          data={[undefined, ...Object.values(AreaType)
            .sort((a, b) => LocationSubtypeTitles[a].localeCompare(LocationSubtypeTitles[b]))]}
          renderItem={({ item }) => {
            const selected: boolean = item === locationType;
            return <Pressable
              style={(state: PressableState) => [
                styles.locationTypeDropdownItem,
                state.hovered && Style.listItemHovered, state.pressed && Style.listItemPressed,
                selected && styles.itemSelected,
              ]}
              onPress={() => {
                setLocationType(item);
                setLocationTypeDrodpdownShown(false);
              }}
            >
              <Text style={[styles.itemText, selected && styles.itemTextSelected]}>
                {LocationSubtypeTitles[item] ?? 'Miscellaneous'}
              </Text>
            </Pressable>;
          }}
          keyExtractor={(_, index) => `area_location_type_item_${index}`}
        />}
      </View>
      {type === 'space' && <View style={styles.infoSectionContainer}>
        <View style={styles.infoSection}>
          <View style={styles.infoIcon}>
            <AlertIcon12 />
          </View>
          <Text style={styles.infoText}>
            {'Please note that you cannot create anything directly inside a space, including queues.'}
            {' If you intend to include a queue, first create the area, '}
            {'and then create a space with \'Queue\' as the location type.'}
          </Text>
        </View>
        {/* TODO: If meeting area type is not reinstated, remove below info blob */}
        {false && <View style={styles.infoSection}>
          <View style={styles.infoIcon}>
            <AlertIcon12 />
          </View>
          <Text style={styles.infoText}>
            {'Please note that this space is located within a meeting area.\n'}
            {'As such, this space can only be a \'Meeting room\', \'Queue\' or of no type'}
          </Text>
        </View>}
      </View>}
      {!!locationType && <CustomTextField
        required
        title='Capacity'
        maxLength={3}
        invalid={!!capacityError}
        errorText={capacityError}
        value={capacity?.toString()}
        updateValue={updateCapacity}
        placeholder='Enter capacity'
      />}
      <CustomTextField
        title='External ID'
        maxLength={maxNameLength}
        invalid={!!externalIdError}
        errorText={externalIdError}
        value={externalId}
        updateValue={updateExternalId}
        editable={clientId !== 'lazard' || externalId === null}
        placeholder='Enter external ID'
      />
      <View style={styles.buttonContainer}>
        <Button
          title='Cancel'
          onPress={hide}
          testID='create-area-modal-cancel-btn'
        />
        <Button
          title={`${editing ? 'Edit' : 'Create'} ${type}`}
          {...{ disabled }}
          affirmative
          children={loading && <ActivityIndicator style={styles.activityIndicator} />}
          onPress={() => save(name, isHighPriority, locationType, capacity, externalId)}
          testID={editing ? 'map-edit-area-modal-btn' : 'map-create-area-btn'}
        />
      </View>
    </View>
  </Modal>;
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'center',
    padding: 40,
    paddingTop: 60,
    alignItems: 'stretch',
    width: 450,
  },
  title: {
    fontSize: 28,
    lineHeight: 35,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
    marginBottom: 32,
    marginTop: 12,
  },
  text: {
    fontSize: 16,
    lineHeight: 24,
    marginTop: 16,
    marginBottom: 4,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  locationTypeDropdownButton: {
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: '#FFF',
    paddingHorizontal: 12,
    paddingVertical: 9,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 44,
  },
  locationTypeDropdown: {
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    marginTop: 6,
    position: 'absolute',
    backgroundColor: '#FFF',
    width: '100%',
    maxHeight: 180,
  },
  locationTypeDropdownItem: {
    padding: 12,
  },
  itemSelected: {
    backgroundColor: Colors.light.hereworksDarkBlue,
  },
  buttonContainer: {
    marginTop: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  activityIndicator: {
    marginStart: 16,
  },
  priorityIndicator: {
    position: 'absolute',
    end: 10,
    top: 50,
  },
  infoSectionContainer: {
    marginTop: 12,
  },
  infoSection: {
    marginTop: 12,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue50,
    backgroundColor: Colors.light.hereworksWhite100,
  },
  infoIcon: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Colors.light.hereworksBlue500,
    justifyContent: 'center',
    alignItems: 'center',
    marginEnd: 16,
  },
  infoText: {
    fontSize: 16,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  itemText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  itemTextSelected: {
    color: '#FFF',
  },
});

export default AddAreaModal;
